import React from "react"
import { composeSx } from "../theme/composeSx"
import {
  IconButton,
  InputAdornment,
  TextField,
  type TextFieldProps,
} from "@mui/material"
import { colors } from "../theme/tokens"
import { resolveViraPalette } from "../theme/resolveViraPalette"
import { ViraIcon } from "./ViraIcon"

export type ViraTextFieldProps = TextFieldProps

/** Outlined text field styled for Vira; safe outside `ViraThemeProvider`. */
export function ViraTextField({ sx, ...rest }: ViraTextFieldProps) {
  return (
    <TextField
      sx={composeSx((theme) => {
        const vira = resolveViraPalette(theme)
        return {
          "& .MuiOutlinedInput-root": {
            borderRadius: "8px",
            fontFamily: '"Lato", sans-serif',
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            "& fieldset": { borderColor: vira.softBorder },
            "&:hover fieldset": { borderColor: colors.primary.ice },
            "&.Mui-focused fieldset": {
              borderColor: colors.primary.periwinkle,
            },
          },
        }
      }, sx)}
      {...rest}
    />
  )
}

export type SearchInputProps = Omit<TextFieldProps, "type"> & {
  onClear?: () => void
}

export function SearchInput({
  placeholder = "Search",
  sx,
  InputProps,
  onClear,
  value,
  ...rest
}: SearchInputProps) {
  const hasValue = value != null && String(value).length > 0
  // Controlled: only show clear when there is a value. Uncontrolled: show when onClear is provided.
  const showClear = Boolean(onClear) && (value === undefined || hasValue)

  const {
    startAdornment: consumerStartAdornment,
    endAdornment: consumerEndAdornment,
    ...restInputProps
  } = InputProps ?? {}

  return (
    <ViraTextField
      type="search"
      placeholder={placeholder}
      size="small"
      value={value}
      InputProps={{
        ...restInputProps,
        startAdornment: consumerStartAdornment ?? (
          <InputAdornment position="start">
            <ViraIcon name="search" tone="subtle" fontSize="small" />
          </InputAdornment>
        ),
        endAdornment:
          showClear || consumerEndAdornment ? (
            <InputAdornment position="end">
              {showClear ? (
                <IconButton
                  aria-label="Clear search"
                  edge="end"
                  size="small"
                  onClick={onClear}
                >
                  <ViraIcon name="close" tone="subtle" fontSize="small" />
                </IconButton>
              ) : null}
              {consumerEndAdornment}
            </InputAdornment>
          ) : undefined,
      }}
      sx={composeSx(
        (theme) => ({
          "& .MuiOutlinedInput-root": {
            backgroundColor: theme.palette.background.paper,
            borderRadius: "8px",
          },
        }),
        sx,
      )}
      {...rest}
    />
  )
}
